import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type { ConsumerTokenRequestParams } from 'owa-tokenprovider-utils';
import sleep from 'owa-sleep';
import { getOrigin } from 'owa-url/lib/getOrigin';
import type { LogStartPerformanceUsage } from 'owa-analytics-start';
import { getResourceToken } from './getResourceToken';
import { fetchTokenFromServer } from './fetchTokenFromServer';

export async function fetchTokenFromHostAppForConsumerWithRetry(
    perfLog: LogStartPerformanceUsage,
    retryCount: number,
    retryDelay: number,
    consumerParams: ConsumerTokenRequestParams
): Promise<TokenResponse> {
    if (retryCount < 1) {
        const error = new Error('Max retrying limit (1) reached');
        perfLog.addCustomProperty('FetchFromHostAppConsumer_Message', error?.message ?? '');
        perfLog.addCustomProperty('FetchFromHostAppConsumer_Stack', error?.stack ?? '');
        return Promise.reject(error);
    }

    return fetchTokenFromHostAppForConsumer(perfLog, consumerParams).catch(error => {
        // Retry only if attempts remain. On the final attempt, reject with the real error.
        if (retryCount > 1) {
            return sleep(retryDelay).then(() => {
                return fetchTokenFromHostAppForConsumerWithRetry(
                    perfLog,
                    retryCount - 1,
                    retryDelay * 2,
                    {
                        ...consumerParams,
                    }
                );
            });
        }

        perfLog.addCustomProperty('FetchFromHostAppConsumer_Message', error?.message ?? '');
        perfLog.addCustomProperty('FetchFromHostAppConsumer_Stack', error?.stack ?? '');
        return Promise.reject(error);
    });
}

export async function fetchTokenFromHostAppForConsumer(
    perfLog: LogStartPerformanceUsage,
    consumerParams: ConsumerTokenRequestParams
): Promise<TokenResponse> {
    perfLog.addCheckpoint('TokenRetrievalFromHostApp_Start');
    const token = await getResourceToken(
        getOrigin(),
        consumerParams.wwwAuthenticateHeader,
        consumerParams.mailboxInfo,
        consumerParams.scope,
        consumerParams.requestId,
        consumerParams.claims
    );

    if (token) {
        perfLog.addCheckpoint('TokenRetrievalFromHostApp_End');
        return token;
    }

    perfLog.addCheckpoint('FetchingSTSToken');
    return fetchTokenFromServer(perfLog, consumerParams);
}
